import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';

import { useAuth } from '../../contexts/AuthContext';
import { createApiClient } from '../../services/apiClient';
import {
  parseSpreadsheetFile,
  autoDetectColumnMapping,
  executeBatchLookup,
} from '../../services/batchLookupService';

import FileUploader from './FileUploader';
import ColumnMapper from './ColumnMapper';
import MatchResultsTable from './MatchResultsTable';
import ExportActions from './ExportActions';

const BatchDiagnosisLookup = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const apiClient = useMemo(() => {
    if (!isAuthenticated) return null;
    const token = sessionStorage.getItem('token');
    return createApiClient(token);
  }, [isAuthenticated]);

  const handleFileSelect = async (file) => {
    setLoading(true);
    setError('');
    try {
      setSelectedFile(file);
      const { headers: parsedHeaders, rows: parsedRows } = await parseSpreadsheetFile(file);

      if (parsedHeaders.length === 0) {
        throw new Error(t('The uploaded file does not contain any headers. Please check the file.'));
      }

      setHeaders(parsedHeaders);
      setRows(parsedRows);

      // Auto-detect columns
      const detectedMapping = autoDetectColumnMapping(parsedHeaders);
      setMapping(detectedMapping);
      setResults([]);
      setSummary(null);
      setActiveStep(0);
    } catch (err) {
      console.error('File parsing error:', err);
      setError(err.message || t('Failed to read the file. Please verify it is a valid Excel or CSV.'));
      setSelectedFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setHeaders([]);
    setRows([]);
    setMapping({});
    setResults([]);
    setSummary(null);
    setError('');
    setActiveStep(0);
  };

  const handleExecuteLookup = async () => {
    if (!apiClient || rows.length === 0) return;

    setLoading(true);
    setError('');
    try {
      const response = await executeBatchLookup(apiClient, rows, mapping);
      setResults(response.results || []);
      setSummary(response.summary || {});
      setActiveStep(1); // Move to Results step
    } catch (err) {
      console.error('Batch lookup execution error:', err);
      setError(err.response?.data?.error || err.message || t('Failed to match diagnoses.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCandidate = (rowIndex, selectedCandidate) => {
    setResults((prevResults) => {
      return prevResults.map((item) => {
        if (item.rowIndex === rowIndex) {
          return {
            ...item,
            status: 'EXACT_MATCH',
            matchConfidence: 'high',
            matchedPatient: {
              id: selectedCandidate.id,
              first_name: selectedCandidate.first_name,
              last_name: selectedCandidate.last_name,
              fullName: selectedCandidate.fullName,
              diagnosis: selectedCandidate.diagnosis || '',
              amka: selectedCandidate.amka || '',
              date_birth: selectedCandidate.date_birth,
              age: selectedCandidate.age,
              phone: selectedCandidate.phone || '',
              last_visit: selectedCandidate.last_visit,
              latestVisitDiagnosis: selectedCandidate.latestVisitDiagnosis || '',
            },
          };
        }
        return item;
      });
    });
  };

  const handleStartNew = () => {
    handleClearFile();
  };

  const steps = [
    t('1. Import & Map Columns'),
    t('2. Match Diagnoses & Export'),
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(156, 39, 176, 0.05) 100%)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  bgcolor: 'primary.main',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                }}
              >
                <SearchIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {t('Batch Diagnosis Lookup & Export')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('Upload an Excel or CSV list of names to automatically find their clinical diagnoses and export.')}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Stepper Indicator */}
          <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{ '& .MuiStepLabel-label': { fontWeight: 600, fontSize: '0.8rem' } }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Stack>
      </Paper>

      {/* Main Workflow View */}
      {activeStep === 0 ? (
        <Stack spacing={3}>
          {/* Step 1A: File Uploader */}
          <FileUploader
            onFileSelect={handleFileSelect}
            loading={loading}
            selectedFile={selectedFile}
            onClearFile={handleClearFile}
            error={error}
          />

          {/* Step 1B: Column Mapper (Appears once file is loaded) */}
          {selectedFile && headers.length > 0 && (
            <ColumnMapper
              headers={headers}
              rows={rows}
              mapping={mapping}
              onMappingChange={setMapping}
              onExecuteLookup={handleExecuteLookup}
              loading={loading}
            />
          )}
        </Stack>
      ) : (
        <Stack spacing={3}>
          {/* Results Action Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ReplayIcon />}
                  onClick={handleStartNew}
                  sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                >
                  {t('New File Lookup')}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setActiveStep(0)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t('Adjust Mapping')}
                </Button>
              </Stack>

              {/* Export Controls */}
              <ExportActions results={results} disabled={loading} />
            </Stack>
          </Paper>

          {/* Results Table */}
          <MatchResultsTable
            results={results}
            summary={summary}
            onSelectCandidate={handleSelectCandidate}
          />
        </Stack>
      )}
    </Box>
  );
};

export default BatchDiagnosisLookup;
